import React, { useContext, useEffect, useState } from 'react';
import { Select, Option, Card, BaseTable, Label, RangeDatePicker, Button, Variants, Flexbox, Divider, RangePickerValueType, Modal, Icons } from '@sede-enterprise/shell-ds-react-framework';
import { Col, Row } from '../../components/flexbox/Flex';
import { ContainerDiv } from '../../components/styledComponent/StyledComponent';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import './styles.css'; 
import { ApiUrls } from '../../helpers/ApiUrls';
import { postData } from '../../services/ApiService';
import { formatDateToDDMMMYYYY, formatDateToYYYYMMDD, usePageTitle } from '../../services/UtilService';
import AppContext from '../../components/applayout/AppContext';
import ResponseErrorModal from '../../components/responseErrorModal/ResponseErrorModal';
import Loader from '../../components/loader/Loader';
import { APIErrorResponseInterface } from '../../helpers/InterfacesConstants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RouteEnum } from '../../app/routeConstants';

interface MissindData{
    country: string;
    lastFile: string;
    missingDays: number;
    site: string;
    dateMissing: number;
}

interface MissingTransactionDatesResponse {
    country: string;
    site: string;
    dayTransactionMissing: number;
    dateMissingCount: number;
    missingTransactionDates: string[];
}


interface DashboardDetails {
    miAgingCases: number;
    totalTransaction: number;
    processedTransaction:number;
    transactionInMI:number;
    reverseAndCorrection: number;
    passthrough: number;
    miRequestedByCategory: {
        customerAndContract: number;
        duplicateTickets: number;
        miRequestedByBusiness: number;
        others: number;
        productAndCharges: number;
        tcCheck: number;
    }
    missingDaysDetails: [{
        country: string;
        lastFile: string;
        missingDays: number;
        site: string;
        dateMissing: number;
    }]
}

interface DashboardNavigationFilters {
    countryIds: string[];
    airportIds: string[];
    dateFrom: string;
    dateTo: string;
    showResultBy: string;
    errorTypes?: string[];
    operationalStatusIds?: string[];
}


const Dashboard = () => {

    const [dashboardDetails, setDashboardDetails] =useState<DashboardDetails>();
    const navigate = useNavigate();
    const appFilter = useContext(AppContext);
    const [countries, setCountries] = useState([]);
    const [dateRange, setDateRange] = useState<RangePickerValueType>();
    const [airports, setAirports] = useState([]);
    const [apiErrorResponse, setApiErrorResponse] = useState<APIErrorResponseInterface | null>();
    const [dateRangeDropdownValue, setDateRangeDropdownValue] = useState('');
    const [missingTransactionDatesModal, setMissingTransactionDatesModal] = useState<{
        open: boolean;
        loading: boolean;
        site: string;
        dates: string[];
    }>({
        open: false,
        loading: false,
        site: '',
        dates: []
    });
    const columns: ColumnDef<MissindData>[] = [{
        header: 'Country',
        accessorKey: 'country'
    }, {
        header: 'Airport Site',
        accessorKey: 'site'
    }, {
        header: 'Last File',
        accessorKey: 'lastFile',
        cell: (info) => formatDateToDDMMMYYYY(String(info.getValue())),
    }, {
        header: 'Missing Days',
        accessorKey: 'missingDays'
    }, {
        header: 'Missing Dates',
        accessorKey: 'dateMissing',
        cell: ({ row }) => {
            const missingDates = Number(row.original.dateMissing || 0);

            return (
                <Flexbox alignItems="center" columnGap="6px" justifyContent="flex-start">
                    <span>{missingDates}</span>
                    {missingDates > 0 ? (
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Icons.InfoCircle
                                style={{ cursor: 'pointer', fontSize: 16, display: 'block' }}
                                onClick={() => { getMissingTransactionDates(row.original); }}
                            />
                        </span>
                    ) : null}
                </Flexbox>
            );
        }
    }];

    const [isLoading, setIsLoading] = useState(true);
    const [selectedFilters, setSelectedFilters] = useState({
        "site": "",
        "country": "",
        "dateFrom": "",
        "dateTo": "",
        "showResultBy": ""
    });
    const { t } = useTranslation();
    usePageTitle(t('shellAviationAIMS')+" | Dashboard");
    
    const getDashboardDetails = (data:{
        "site": string;
        "country": string;
        "dateFrom": string;
        "dateTo": string;
        "showResultBy": string;
    }) => {
        const url = ApiUrls.dashboardDetails;
        setIsLoading(true);
        postData(url, data).then((response) => {
            setDashboardDetails(response.data);
            setIsLoading(false);
        }).catch((error)=>{
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
        });
    }

    useEffect(() => {
        if(appFilter){
            setCountries(JSON.parse(appFilter).countries);
            setAirports(JSON.parse(appFilter).airports);
        }
    }, [appFilter]);

    useEffect(() => {
        const data = {
            "site": "",
            "country": "",
            "dateFrom": "",
            "dateTo": "",
            "showResultBy": ""
        };
        getDashboardDetails(data);
    }, []);


    const onCountryChange = (value: string) =>{
        const data ={
            ...selectedFilters,
            site:'',
            country: value ? value : ''
        }

        if(value){
            const tempAiports: any = [];
            JSON.parse(appFilter).airports.filter( (airport: any) => {
                if(value == airport.countryCode){
                    tempAiports.push(airport); 
                }
            });
            setAirports(tempAiports);
        }else{
            setAirports(JSON.parse(appFilter).airports);
        }

    
        setSelectedFilters(data);
        getDashboardDetails(data);
    }

    const onAirportChange = (value: string) =>{
        const data ={
            ...selectedFilters,
            site: value ? value : ''
        }
        setSelectedFilters(data);
        getDashboardDetails(data);
    }


    const showResultBy = (value: string) => {
        const data ={
            ...selectedFilters,
            showResultBy: value ? value : ''
        }
        setSelectedFilters(data);
        getDashboardDetails(data);
    }

    const onDateRangeChange = (range: string) => {
        setDateRangeDropdownValue(range);
        const today = new Date();
        const previousDate = new Date();
        if(range){
            switch(range){
                case '24hours': 
                previousDate.setDate(today.getDate() - 1);
                break;
    
                case '3days': 
                previousDate.setDate(today.getDate() - 3);
                break;
    
                case '7days': 
                previousDate.setDate(today.getDate() - 7);
                break;
    
                case '30days': 
                previousDate.setDate(today.getDate() - 30);
                break;
            }
            const data = {
                ...selectedFilters,
                dateFrom: formatDateToYYYYMMDD(previousDate)+'',
                dateTo:  formatDateToYYYYMMDD(today)+''
            }
            setSelectedFilters(data);
            setDateRange([dayjs(data.dateFrom), dayjs(data.dateTo)]);
            getDashboardDetails(data);
        }else{
            const data = {
                ...selectedFilters,
                dateFrom: '',
                dateTo:  ''
            }
            setSelectedFilters(data);
            setDateRange(null);
            getDashboardDetails(data);
        }
    }

    const onDateChange = (value: RangePickerValueType) => {
        let data;
        const hasCompleteRange = !!value?.[0] && !!value?.[1];

        if (hasCompleteRange) {
            setDateRange(value);
            data = {
                ...selectedFilters,
                dateFrom: formatDateToYYYYMMDD(dayjs(value[0]).toDate())+'',
                dateTo: formatDateToYYYYMMDD(dayjs(value[1]).toDate())+''
            }
            setSelectedFilters(data);
        } else {
            setDateRange(null);
            data = {
                ...selectedFilters,
                dateFrom: '',
                dateTo:''
            }
            setSelectedFilters(data);
        }
        setDateRangeDropdownValue('');
        getDashboardDetails(data);
    }

    const onReset = () => {
        const data = {
            "site": "",
            "country": "",
            "dateFrom": "",
            "dateTo": "",
            "showResultBy": ""
        };

        setSelectedFilters(data);
        setDateRange(null);
        setDateRangeDropdownValue('');
        getDashboardDetails(data);
    }

    const buildCommonNavigationFilters = (): DashboardNavigationFilters => ({
        countryIds: selectedFilters.country ? [selectedFilters.country] : [],
        airportIds: selectedFilters.site ? [selectedFilters.site] : [],
        dateFrom: selectedFilters.dateFrom,
        dateTo: selectedFilters.dateTo,
        showResultBy: selectedFilters.showResultBy || 'ReceivedDate'
    });

    const navigateToTransactions = (path: RouteEnum, extraFilters: Partial<DashboardNavigationFilters> = {}) => {
        navigate(path, {
            state: {
                ...buildCommonNavigationFilters(),
                ...extraFilters
            }
        });
    }

    const closeMissingTransactionDatesModal = () => {
        setMissingTransactionDatesModal({
            open: false,
            loading: false,
            site: '',
            dates: []
        });
    }

    const getMissingTransactionDates = (row: MissindData) => {
        setMissingTransactionDatesModal({
            open: true,
            loading: true,
            site: row.site,
            dates: []
        });

        postData(ApiUrls.dashboardMissingTransactionDates, {
            site: row.site,
            country: row.country
        }).then((response) => {
            const responseData: MissingTransactionDatesResponse = response.data?.data ?? response.data;
            setMissingTransactionDatesModal({
                open: true,
                loading: false,
                site: responseData?.site || row.site,
                dates: Array.isArray(responseData?.missingTransactionDates) ? responseData.missingTransactionDates : []
            });
        }).catch((error) => {
            console.error('Failed to load missing transaction dates', error);
            closeMissingTransactionDatesModal();
        });
    }

    const renderTileLinkIndicator = () => (
        <div className='dashboard-tile-indicator'>
            <Icons.Link style={{ fontSize: 16 }} />
        </div>
    )

    return (
        <>
            <div className="dashboard-container">
            <Row>
                <Col size="25">
                    <div style={{backgroundColor:'#FFFFFF', height:'100%', marginTop: '0px'}}>
                        <ContainerDiv>
                            <Label bold>Country</Label>
                            <Select onChange={(e)=>{onCountryChange(e)}} value={selectedFilters.country} >
                            {countries.map((country:{id:string;name:string}) => (
                                <Option key={country.id} value={country.id}>{country.name}</Option>
                            ))}
                            </Select>
                        </ContainerDiv>
                        <ContainerDiv>
                            <Label bold>Airport Site</Label>
                            <Select onChange={(e)=>{onAirportChange(e)}} value={selectedFilters.site}>
                            {airports.map((airport:{id:string;name:string}) => (
                                <Option key={airport.id} value={airport.id}>{airport.name}</Option>
                            ))}
                            </Select>
                        </ContainerDiv>
                        <ContainerDiv>
                            <Label bold>Date Range</Label>
                            <Select onChange={(e)=>{onDateRangeChange(e)}} value={dateRangeDropdownValue}>
                                <Option value="24hours">Last 24 Hours</Option>
                                <Option value="3days">Last 3 Days</Option>
                                <Option value="7days">Last 7 Days</Option>
                                <Option value="30days">Last 30 Days</Option>
                            </Select>
                            <RangeDatePicker placeholder={['Start date', 'Finish date']} style={{marginTop:'15px'}} onChange={(value)=>{onDateChange(value)}} value={dateRange} aria-label="Date Range picker"/>
                        </ContainerDiv>
                        <ContainerDiv>
                            <Label bold>Show Results By</Label>
                            <Select onChange={(e)=>{showResultBy(e)}} value={selectedFilters.showResultBy}>
                                <Option value="TransactionDate">Transaction Date</Option>
                                <Option value="ReceivedDate">Received Date</Option>
                            </Select>
                        </ContainerDiv>
                        <ContainerDiv>
                            <Button variant={Variants.Outlined} onClick={onReset}>Reset</Button>
                        </ContainerDiv>
                    </div>
                </Col>
                <Col size="75">

                    {isLoading && <Loader/>}
                    
                    <div style={{padding:'10px', backgroundColor: '#F5F5F5'}}>
                        <Row>
                            <Flexbox style={{width:'100%', gap:'10px'}}>
                                <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.rawdata)}}>
                                    <Card className='card'>
                                        <div className='dashboard-tile-content'>
                                            <div>
                                                <h1 style={{color:'#336094'}}>{dashboardDetails?.totalTransaction}</h1>
                                                <h3>Total Transactions</h3>
                                            </div>
                                            {renderTileLinkIndicator()}
                                        </div>
                                    </Card>
                                </div>
                                <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.rawdata, { operationalStatusIds: ['101','81'] })}}>
                                    <Card className='card'>
                                        <div className='dashboard-tile-content'>
                                            <div>
                                                <h1 style={{color:'#008557'}}>{dashboardDetails?.processedTransaction}</h1>
                                                <h3>Processed Transactions</h3>
                                            </div>
                                            {renderTileLinkIndicator()}
                                        </div>
                                    </Card>
                                </div>
                                <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions)}}>
                                    <Card className='card'>
                                        <div className='dashboard-tile-content'>
                                            <div>
                                                <h1 style={{color:'#DD1D21'}}>{dashboardDetails?.transactionInMI}</h1>
                                                <h3>Transactions in MI</h3>
                                            </div>
                                            {renderTileLinkIndicator()}
                                        </div>
                                    </Card>
                                </div>
                            </Flexbox>
                        </Row>

                        <Card header={{title: 'MI Transactions by Category'}} className='mi-transaction-card'>
                            <Row>
                                <Flexbox style={{width:'100%', gap:'10px'}}>
                                    <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions, { errorTypes: ['7'] })}}>
                                        <Card className='tc-check-card card'>
                                            <div className='dashboard-tile-content'>
                                                <div>
                                                    <h1 style={{color:'#DD1D21'}}>{dashboardDetails?.miRequestedByCategory.tcCheck}</h1>

                                                    <Divider style={{marginTop: '15px'}}/>
                                                    <h4>Trade Control Check</h4>
                                                </div>
                                                {renderTileLinkIndicator()}
                                            </div>
                                        </Card>
                                    </div>
                                    <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions, { errorTypes: ['6'] })}}>
                                        <Card className='mi-requested-card card'>
                                            <div className='dashboard-tile-content'>
                                                <div>
                                                    <h1 style={{color:'rgb(155, 91, 3)'}}>{dashboardDetails?.miRequestedByCategory.miRequestedByBusiness}</h1>

                                                    <Divider style={{marginTop: '15px'}}/>
                                                    <h4>MI Requested by Business</h4>
                                                </div>
                                                {renderTileLinkIndicator()}
                                            </div>
                                        </Card>
                                    </div>
                                    <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions, { errorTypes: ['2'] })}}>
                                        <Card className='product-charges-card card'>
                                            <div className='dashboard-tile-content'>
                                                <div>
                                                    <h1 style={{color:'#DD1D21'}}>{dashboardDetails?.miRequestedByCategory.productAndCharges}</h1>

                                                    <Divider style={{marginTop: '15px'}}/>
                                                    <h4>Product & Charges</h4>
                                                </div>
                                                {renderTileLinkIndicator()}
                                            </div>
                                        </Card>
                                    </div>
                                    <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions, { errorTypes: ['4'] })}}>
                                        <Card className='customer-contract-card card'>
                                            <div className='dashboard-tile-content'>
                                                <div>
                                                    <h1 style={{color:'#DD1D21'}}>{dashboardDetails?.miRequestedByCategory.customerAndContract}</h1>

                                                    <Divider style={{marginTop: '15px'}}/>
                                                    <h4>Customer & Contract</h4>
                                                </div>
                                                {renderTileLinkIndicator()}
                                            </div>
                                        </Card>
                                    </div>
                                    <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.transactions, { errorTypes: ['5'] })}}>
                                        <Card className='others-card card'>
                                            <div className='dashboard-tile-content'>
                                                <div>
                                                    <h1 style={{color:'#DD1D21'}}>{dashboardDetails?.miRequestedByCategory.others}</h1>

                                                    <Divider style={{marginTop: '15px'}}/>
                                                    <h4>Others</h4>
                                                </div>
                                                {renderTileLinkIndicator()}
                                            </div>
                                        </Card>
                                    </div>
                                </Flexbox>
                            </Row>
                        </Card>

                        <Row>
                            <Flexbox style={{width:'100%', gap:'10px'}}>
                                <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.rawdata, { operationalStatusIds: ['112'] })}}>
                                    <Card>
                                        <div className='dashboard-tile-content'>
                                            <div>
                                                <h3><span>Duplicate Tickets</span> <span style={{float:'right', color:'#336094'}}>{dashboardDetails?.miRequestedByCategory.duplicateTickets}</span></h3>
                                            </div>
                                            {renderTileLinkIndicator()}
                                        </div>
                                    </Card>
                                </div>
                                <div className='dashboard-tile-link' onClick={() => {navigateToTransactions(RouteEnum.rawdata, { operationalStatusIds: ['114'] })}}>
                                    <Card>
                                        <div className='dashboard-tile-content'>
                                            <div>
                                                <h3><span>Passthrough</span> <span style={{float:'right', color:'#336094'}}>{dashboardDetails?.passthrough}</span></h3>
                                            </div>
                                            {renderTileLinkIndicator()}
                                        </div>
                                    </Card>
                                </div>
                                <div className='dashboard-tile-link dashboard-tile-static'>
                                    <Card><h3><span>MI Aging Cases</span> <span style={{float:'right', color:'#DD1D21'}}>{dashboardDetails?.miAgingCases}</span></h3></Card>
                                </div>
                            </Flexbox>
                        </Row>

                        <Card header={{title: 'Missing Days'}} style={{marginTop: '10px', marginBottom:'20px'}}>
                            <Row>
                                <BaseTable data={dashboardDetails?.missingDaysDetails} columns={columns} noFooter />
                            </Row>
                        </Card>
                    </div>
                </Col>
            </Row>
            </div>

            <Modal
                open={missingTransactionDatesModal.open}
                onClose={closeMissingTransactionDatesModal}
                title={<span>Missing Transaction Dates for <span style={{ textDecoration: 'underline' }}>{missingTransactionDatesModal.site}</span></span>}
            >
                <div style={{ marginTop: '-18px' }}>
                    {missingTransactionDatesModal.loading ? (
                        <Loader />
                    ) : missingTransactionDatesModal.dates.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {missingTransactionDatesModal.dates.map((dateValue, index) => (
                                <li key={`${dateValue}-${index}`}>
                                    {dayjs(dateValue).isValid() ? dayjs(dateValue).format('DD MMM YYYY') : dateValue}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div>No missing transaction dates found.</div>
                    )}
                </div>
            </Modal>

            <ResponseErrorModal errorDetails={apiErrorResponse} isOpen={apiErrorResponse ? true : false} onModalClose={()=>{setApiErrorResponse(null)}}></ResponseErrorModal>
        </>
    )
}

export default Dashboard;